import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

function StockBadge({ stock }) {
  if (stock === 0)  return <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700">Out of Stock</span>;
  if (stock <= 10)  return <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700">Low Stock</span>;
  return                   <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">In Stock</span>;
}

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");
  const [editing,  setEditing]  = useState({});
  const [saving,   setSaving]   = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res  = await fetch(`${API}/api/admin/inventory`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const data = await res.json();
        if (!cancelled) {
  const unique = data.filter((p, idx, self) => 
    idx === self.findIndex(x => x.id === p.id)
  );
  setProducts(unique);
}
      } catch {
        if (!cancelled) setError("Failed to load inventory.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  async function handleStockSave(id) {
    const newStock = parseInt(editing[id]);
    if (isNaN(newStock) || newStock < 0) return;
    setSaving(id);
    try {
      await fetch(`${API}/api/admin/inventory/${id}/stock`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ stock: newStock }),
      });
      setProducts(ps => ps.map(p => p.id === id ? { ...p, stock: newStock } : p));
      setEditing(e => { const n = { ...e }; delete n[id]; return n; });
    } catch {
      alert("Failed to update stock.");
    } finally {
      setSaving(null);
    }
  }

  // Stats
  const outOfStock = products.filter(p => p.stock === 0).length;
  const lowStock   = products.filter(p => p.stock > 0 && p.stock <= 10).length;
  const inStock    = products.filter(p => p.stock > 10).length;

  // Filter + Search
  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                        p.category.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === "out" ? p.stock === 0 :
      filter === "low" ? p.stock > 0 && p.stock <= 10 :
      true;
    return matchSearch && matchFilter;
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading inventory...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        <p className="text-gray-500 text-sm mt-1">{products.length} total products</p>
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{inStock}</p>
          <p className="text-xs text-green-500 mt-1 font-medium">In Stock</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{lowStock}</p>
          <p className="text-xs text-yellow-500 mt-1 font-medium">Low Stock (≤10)</p>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{outOfStock}</p>
          <p className="text-xs text-red-500 mt-1 font-medium">Out of Stock</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search products..."
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-rose-400"
        />
        <div className="flex gap-2">
          {[
            { key: "all", label: "All"            },
            { key: "low", label: "⚠️ Low Stock"   },
            { key: "out", label: "❌ Out of Stock" },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition border
                ${filter === f.key
                  ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-gray-600 border-gray-300 hover:border-rose-400"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Product</th>
                <th className="px-5 py-3 text-left">Category</th>
                <th className="px-5 py-3 text-left">Price</th>
                <th className="px-5 py-3 text-left">Stock Status</th>
                <th className="px-5 py-3 text-left">Current Stock</th>
                <th className="px-5 py-3 text-left">Update Stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(p => {
                const imgs = typeof p.images === "string" ? JSON.parse(p.images) : p.images;
                const isEditing = editing[p.id] !== undefined;
                return (
                  <tr key={p.id} className={`hover:bg-gray-50 transition ${p.stock === 0 ? "bg-red-50/30" : p.stock <= 10 ? "bg-yellow-50/30" : ""}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={imgs?.[0]} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                        <p className="font-medium text-gray-900">{p.name}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{p.category}</td>
                    <td className="px-5 py-3 font-semibold text-gray-900">${Number(p.price).toLocaleString()}</td>
                    <td className="px-5 py-3"><StockBadge stock={p.stock} /></td>
                    <td className="px-5 py-3">
                      <span className="text-lg font-bold text-gray-900">{p.stock}</span>
                      <span className="text-xs text-gray-400 ml-1">units</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="0"
                          value={isEditing ? editing[p.id] : p.stock}
                          onFocus={() => {
                            if (!isEditing) setEditing(ed => ({ ...ed, [p.id]: String(p.stock) }));
                          }}
                          onChange={e => setEditing(ed => ({ ...ed, [p.id]: e.target.value }))}
                          className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-rose-400"
                        />
                        {isEditing && (
                          <>
                            <button
                              onClick={() => handleStockSave(p.id)}
                              disabled={saving === p.id}
                              className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition">
                              {saving === p.id ? "..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditing(e => { const n = { ...e }; delete n[p.id]; return n; })}
                              className="text-gray-400 hover:text-gray-600 px-2 py-1.5 rounded-lg text-xs transition">
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}